import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useSelector } from 'react-redux';
import toast from 'react-hot-toast';
import { Form, Input, Checkbox, Spin, Radio } from 'antd';
import LoadableButton from "../../../components/buttons/LoadableButton.jsx";
import Balance from '../../../assets/Balance.jsx';
import Account from '../../../assets/common/Account';
import Dollar from '../../../assets/common/Dollar';
import { GetMT5AccountsApi, GetWalletApi, InternalTransferApi } from '../../../api/request/transfer.js';
import TransferRequest from './TransferRequest.jsx';

const Transfer = () => {
  const { userDetails } = useSelector(state => state.user);
  const [form] = Form.useForm();
  const [wallet, setWallet] = useState({});
  const [accounts, setAccounts] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [fromAccount, setFromAccount] = useState(null);
  const [toAccount, setToAccount] = useState(null);
  const [isSubmitLoading, setIsSubmitLoading] = useState(false);
  const [isRefresh, setIsRefresh] = useState(false);

  const getWallet = async () => {
    setIsLoading(true);
    try {
      const params = { RuleType: 'InternalTransfer' };
      const { data } = await GetWalletApi(userDetails?._id, new URLSearchParams(params).toString());
      if (data?.success) setWallet(data?.data)
    } catch (error) {
    } finally {
      setIsLoading(false);
    }
  }

  const getAccounts = async () => {
    setIsLoading(true);
    try {
      const { data } = await GetMT5AccountsApi(true);
      if (data?.success) {
        setAccounts([
          { login: "wallet" },
          ...data?.data?.filter((account) => account?.isLive),
        ]);
      }
    } catch (error) {
      if (error?.response?.data?.error?.message)
        toast.error(error?.response?.data?.error?.message);
    } finally {
      setIsLoading(false);
    }
  }

  useEffect(() => {
    getWallet();
    getAccounts();
  }, []);

  const resetAll = () => {
    form.resetFields();
    setFromAccount(null);
    setToAccount(null);
  }

  const handleSubmit = async (values) => {
    setIsSubmitLoading(true);
    try {
      const isWalletToAccount = values?.fromAccount === 'wallet';
      const isAccountToWallet = values?.toAccount === 'wallet';
      let data = null;

      if (isWalletToAccount || isAccountToWallet) {
        const res = await InternalTransferApi({
          amount: Number(values?.amount),
          sourceTerminalId: isAccountToWallet ? values?.fromAccount : 0,
          destinationTerminalId: isWalletToAccount ? values?.toAccount : 0,
          transferType: isWalletToAccount ? 1 : 2,
        });
        data = res?.data;
      } else {
        const res = await InternalTransferApi({
          amount: Number(values?.amount),
          sourceTerminalId: values?.fromAccount,
          destinationTerminalId: values?.toAccount,
          transferType: 3,
        });
        data = res?.data;
      }

      if (data?.success) {
        toast.success(data?.message);
        resetAll();
        getWallet();
        getAccounts();
        setIsRefresh(true);
      }
    } catch (error) {
      if (error?.response?.data?.error?.message)
        toast.error(error?.response?.data?.error?.message);
    } finally {
      setIsSubmitLoading(false);
    }
  }

  const validateAmount = (_, value) => {
    if (!value) return Promise.reject(new Error('Please enter amount.'));

    if (Number(value) <= 0)
      return Promise.reject(new Error('Please enter a valid amount.'));

    if (wallet?.isRuleActive) {
      if (value < wallet?.minAmount) {
        return Promise.reject(new Error(`Amount must be at least $${wallet?.minAmount}.`));
      }
      if (value > wallet?.maxAmount) {
        return Promise.reject(new Error(`Amount cannot exceed $${wallet?.maxAmount}.`));
      }
    }

    return Promise.resolve();
  };

  const validateAccount = (_, value) => {
    if (!value) return Promise.resolve();

    if (fromAccount === toAccount) {
      return Promise.reject(new Error('From and To accounts must be different.'));
    }

    return Promise.resolve();
  };

  const renderAccounts = (type, value) => (
    <Radio.Group className="w-full grid grid-cols-1 md:grid-cols-3 xl:grid-cols-6 gap-3">
      {accounts?.map((item, index) => (
        <Radio
          key={index}
          value={item?.login}
          className={`flex items-center gap-1 border-2 rounded-lg p-3 m-0 ${value === item?.login ? 'border-primary' : 'border-light'}`}
          onChange={(e) => type === "from" ? setFromAccount(e?.target?.value) : setToAccount(e?.target?.value)}
        >
          <div className='flex items-center justify-center gap-3'>
            {item?.login === "wallet" ? (
              <Balance color="#00727D" className="size-8" />
            ) : (
              <img
                src="/assets/images/mt5.png"
                alt="Icon"
                className="h-[35px] w-[35px] object-contain"
              />
            )}

            <div>
              <p className="text-lg capitalize">{item?.login}</p>
              {item?.login !== "wallet" && (
                <div>
                  {item?.balance ? (<p className="text-xs ms-1"> Balance: {item?.balance} </p>) : null}
                  {(type === "from" && item?.scalping) ? (
                    <div>
                      <p className="text-xs"> Scalping: {item?.scalping} </p>
                      <p className="text-xs"> Available: {Number(item?.balance) - Number(item?.scalping)} </p>
                    </div>
                  ) : null}
                </div>
              )}
            </div>
          </div>
        </Radio>
      ))}
    </Radio.Group>
  )

  const infoCards = [
    { title: 'Wallet Account No.', value: wallet?.walletAccountNo, color: "bg-[#E5F1F2]", icon: <Account color="#00727D" /> },
    { title: 'Wallet Balance', value: wallet?.walletAmount, prefix: '$', color: 'bg-[#FFE2E3]', icon: <Balance color="#FF4C51" /> },
    { title: 'Minimum Deposit', value: wallet?.minAmount, prefix: '$', color: 'bg-[#FFF0E1]', icon: <Dollar color="#FF9F43" /> },
    { title: 'Maximum Deposit', value: wallet?.maxAmount, prefix: '$', color: 'bg-[#DDF6E8]', icon: <Dollar color="#28C76F" /> },
  ];

  return (
    <div className='space-y-5'>
      <div className='border-2 border-light rounded-lg p-5'>
        <div className="grid md:grid-cols-2 xl:grid-cols-4 gap-5">
          {infoCards?.map((card) => (
            <div className='flex items-center gap-3'>
              <div className={`flex justify-center items-center ${card?.color} w-[45px] h-[45px] rounded-lg`}>
                {card?.icon}
              </div>
              <div>
                <p className='sm:text-xl text-md'>{card?.prefix} {card?.value || 0} {card?.suffix}</p>
                <p className='text-sm font-medium text-gray-500 capitalize'>{card?.title}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className='border-[2px] border-light rounded-xl p-5'>
        <Form form={form} layout='vertical' className='space-y-5' onFinish={handleSubmit}>
          <Form.Item
            name='amount'
            label={<span className="text-lg text-gray-500 font-medium">Amount In USD</span>}
            className='w-full max-w-[500px]'
            rules={[{ validator: validateAmount }]}
          >
            <Input type='number' placeholder="Amount in USD" size='large' />
          </Form.Item>

          <Form.Item
            name="fromAccount"
            label={<span className="text-lg text-gray-500 font-medium">Select From Account</span>}
            rules={[{ required: true, message: 'Please select an account.' }]}
          >
            {renderAccounts("from", fromAccount)}
          </Form.Item>

          <Form.Item
            name="toAccount"
            dependencies={['fromAccount']}
            label={<span className="text-lg text-gray-500 font-medium">Select To Account</span>}
            rules={[{ required: true, message: 'Please select an account.' }, { validator: validateAccount }]}
          >
            {renderAccounts("to", toAccount)}
          </Form.Item>

          <Form.Item
            name='conditions'
            valuePropName="checked"
            rules={[{ validator: (_, value) => value ? Promise.resolve() : Promise.reject(new Error('Please accept the terms and conditions.')) }]}
          >
            <Checkbox className='mt-4'>
              Yes, I agreed to the <Link to={'/terms_&_conditions'} className='primary hover:underline duration-300'>Terms and conditions</Link>
            </Checkbox>
          </Form.Item>

          <LoadableButton
            className='bg-primary text-sm text-white font-semibold rounded-lg px-12 py-2 uppercase themeHover duration-500'
            type="submit"
            lable='Submit'
            isLoading={isSubmitLoading}
            loadingLable='Processing...'
          />
        </Form>
      </div>

      <TransferRequest isRefresh={isRefresh} setIsRefresh={setIsRefresh} />
    </div>
  )
}

export default Transfer
